import { useState, useMemo } from 'react'
import { format, parseISO, differenceInDays } from 'date-fns'
import { useServiceRecords, useFuelLogs, useVehicleReminders, useVehiclePhotoUrl } from '../hooks/useVehicles'
import { useAuth } from '../../../hooks/useAuth'
import { supabase } from '../../../lib/supabase'
import { useQueryClient } from '@tanstack/react-query'
import { ServiceRecordForm } from './ServiceRecordForm'
import { FuelLogForm } from './FuelLogForm'
import { ReminderForm } from './ReminderForm'
import { cn } from '../../../lib/utils'
import { Spinner } from '../../../components/ui/Spinner'
import { getSignedUrl } from '../../../lib/upload'

const today = () => new Date().toISOString().slice(0, 10)

const ReminderStatus = ({ reminder }) => {
  if (!reminder.due_date) return null
  const days = differenceInDays(parseISO(reminder.due_date), new Date())
  if (days < 0) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-600">Overdue</span>
  if (days <= 7) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Due in {days}d</span>
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background text-text-muted">{format(parseISO(reminder.due_date), 'MMM d')}</span>
}

const ServiceTypeBadge = ({ type }) => (
  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-light text-primary capitalize">
    {type.replace(/_/g, ' ')}
  </span>
)

export const VehicleDetail = ({ vehicle, onEdit, onDelete, onClose }) => {
  const { isAdmin, isLimited, user } = useAuth()
  const qc = useQueryClient()
  const { data: photoUrl }      = useVehiclePhotoUrl(vehicle.photo_path)
  const { data: serviceRecords = [], create: createSR, remove: removeSR } = useServiceRecords(vehicle.id)
  const { data: fuelLogs = [],       create: createFL, remove: removeFL } = useFuelLogs(vehicle.id)
  const { data: reminders = [],      create: createRm, update: updateRm, remove: removeRm } = useVehicleReminders(vehicle.id)

  const [tab,           setTab]           = useState('overview')
  const [showSRForm,    setShowSRForm]    = useState(false)
  const [showFLForm,    setShowFLForm]    = useState(false)
  const [showRmForm,    setShowRmForm]    = useState(false)
  const [editingRm,     setEditingRm]     = useState(null)
  const [editingMileage,setEditingMileage]= useState(false)
  const [mileageInput,  setMileageInput]  = useState(vehicle.current_mileage ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting,      setDeleting]      = useState(false)

  const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`

  // MPG from last two full-tank logs
  const mpg = useMemo(() => {
    const fullTanks = [...fuelLogs]
      .filter((l) => l.is_full_tank)
      .sort((a, b) => b.mileage - a.mileage)
    if (fullTanks.length < 2) return null
    const miles = fullTanks[0].mileage - fullTanks[1].mileage
    return miles > 0 ? (miles / parseFloat(fullTanks[0].gallons)).toFixed(1) : null
  }, [fuelLogs])

  // Monthly fuel cost
  const thisMonthFuel = useMemo(() => {
    const now = new Date()
    return fuelLogs
      .filter((l) => {
        const d = parseISO(l.log_date)
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
      })
      .reduce((sum, l) => sum + parseFloat(l.total_cost), 0)
  }, [fuelLogs])

  // Upcoming reminders (within 30 days)
  const upcomingReminders = useMemo(() =>
    reminders.filter((r) => {
      if (!r.due_date) return false
      return differenceInDays(parseISO(r.due_date), new Date()) <= 30
    }).sort((a, b) => parseISO(a.due_date) - parseISO(b.due_date)),
    [reminders]
  )

  const canEdit = !isLimited

  const saveMileage = async () => {
    const val = parseInt(mileageInput)
    if (!isNaN(val) && val > 0) {
      await supabase.from('vehicles').update({ current_mileage: val }).eq('id', vehicle.id)
      qc.invalidateQueries({ queryKey: ['vehicles'] })
    }
    setEditingMileage(false)
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete({ id: vehicle.id, photoPath: vehicle.photo_path })
      onClose()
    } catch {
      setDeleting(false)
    }
  }

  const TABS = ['overview','service','fuel','reminders']

  return (
    <div className="fixed inset-0 z-50 bg-surface flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-border flex-shrink-0">
        <button onClick={onClose} className="text-text-muted hover:text-text-primary flex items-center gap-1.5">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="font-bold text-text-primary">{vehicleName}</h2>
        {canEdit && (
          <button onClick={() => onEdit(vehicle)} className="text-xs text-primary font-medium">Edit</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border flex-shrink-0">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'flex-1 text-xs font-medium py-2.5 capitalize transition-colors border-b-2 -mb-px',
              tab === t ? 'border-primary text-primary' : 'border-transparent text-text-muted'
            )}
          >
            {t === 'service' ? 'Service' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ── Overview ── */}
        {tab === 'overview' && (
          <div className="p-4 space-y-4">
            {/* Photo or silhouette */}
            <div className="w-full h-40 rounded-xl overflow-hidden bg-border/20 flex items-center justify-center">
              {photoUrl ? (
                <img src={photoUrl} alt={vehicleName} className="w-full h-full object-cover" />
              ) : (
                <svg className="w-24 h-24 text-border" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="1" y="3" width="15" height="13" rx="2"/>
                  <path d="m16 8 5 3v5h-5"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
              )}
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Year', vehicle.year],
                ['Make', vehicle.make],
                ['Model', vehicle.model],
                ['Trim', vehicle.trim],
                ['Color', vehicle.color],
                ['License Plate', vehicle.license_plate],
                ['VIN', vehicle.vin],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="bg-background rounded-xl px-3 py-2">
                  <div className="text-xs text-text-muted mb-0.5">{k}</div>
                  <div className="text-sm font-medium text-text-primary">{v}</div>
                </div>
              ))}

              {/* Mileage — editable */}
              <div className="bg-background rounded-xl px-3 py-2">
                <div className="text-xs text-text-muted mb-0.5">Mileage</div>
                {editingMileage ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={mileageInput}
                      onChange={(e) => setMileageInput(e.target.value)}
                      autoFocus
                      className="w-20 text-sm font-medium border border-border rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <button onClick={saveMileage} className="text-xs text-primary font-semibold">Save</button>
                    <button onClick={() => setEditingMileage(false)} className="text-xs text-text-muted">✕</button>
                  </div>
                ) : (
                  <button onClick={() => canEdit && setEditingMileage(true)}
                    className="text-sm font-medium text-text-primary hover:text-primary transition-colors">
                    {vehicle.current_mileage ? `${vehicle.current_mileage.toLocaleString()} mi` : '—'}
                  </button>
                )}
              </div>
            </div>

            {/* Primary driver */}
            {vehicle.profiles && (
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                <span>{vehicle.profiles.display_name}</span>
              </div>
            )}

            {/* Upcoming reminders */}
            {upcomingReminders.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Upcoming Reminders</h3>
                <div className="space-y-2">
                  {upcomingReminders.map((r) => (
                    <div key={r.id} className="flex items-center justify-between rounded-xl bg-background px-3 py-2.5">
                      <span className="text-sm text-text-primary">{r.reminder_name}</span>
                      <ReminderStatus reminder={r} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Delete */}
            {canEdit && (
              <div className="pt-2">
                {confirmDelete ? (
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmDelete(false)}
                      className="flex-1 border border-border rounded-xl py-2.5 text-sm font-medium text-text-secondary">Cancel</button>
                    <button onClick={handleDelete} disabled={deleting}
                      className="flex-1 bg-rose-500 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                      {deleting ? <Spinner size="sm" /> : 'Confirm Delete'}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDelete(true)}
                    className="w-full border border-rose-200 text-rose-500 rounded-xl py-2.5 text-sm font-semibold">
                    Delete Vehicle
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Service Records ── */}
        {tab === 'service' && (
          <div className="p-4">
            {canEdit && (
              <button onClick={() => setShowSRForm(true)}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white rounded-xl py-2.5 text-sm font-medium mb-4">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add Service Record
              </button>
            )}
            {serviceRecords.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-8">No service records yet.</p>
            ) : (
              <div className="space-y-3">
                {serviceRecords.map((r) => (
                  <div key={r.id} className="bg-background rounded-xl p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ServiceTypeBadge type={r.service_type} />
                        <span className="text-xs text-text-muted">
                          {format(parseISO(r.service_date), 'MMM d, yyyy')}
                        </span>
                        {r.mileage_at_service && (
                          <span className="text-xs text-text-muted">{r.mileage_at_service.toLocaleString()} mi</span>
                        )}
                      </div>
                      {canEdit && (
                        <button onClick={() => removeSR.mutate({ id: r.id, receiptPath: r.receipt_path })}
                          className="text-text-muted hover:text-rose-500 flex-shrink-0">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    {r.description && <p className="text-sm text-text-primary">{r.description}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
                      {r.provider && <span>{r.provider}</span>}
                      {r.cost && <span>${parseFloat(r.cost).toFixed(2)}</span>}
                      {r.next_service_date && <span>Next: {format(parseISO(r.next_service_date), 'MMM d, yyyy')}</span>}
                      {r.next_service_mileage && <span>Next: {r.next_service_mileage.toLocaleString()} mi</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Fuel Log ── */}
        {tab === 'fuel' && (
          <div className="p-4">
            {/* MPG + monthly summary */}
            {(mpg || thisMonthFuel > 0) && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {mpg && (
                  <div className="bg-background rounded-xl px-3 py-3 text-center">
                    <div className="text-2xl font-bold text-primary">{mpg}</div>
                    <div className="text-xs text-text-muted">MPG (last fill-up)</div>
                  </div>
                )}
                {thisMonthFuel > 0 && (
                  <div className="bg-background rounded-xl px-3 py-3 text-center">
                    <div className="text-2xl font-bold text-text-primary">${thisMonthFuel.toFixed(0)}</div>
                    <div className="text-xs text-text-muted">Fuel this month</div>
                  </div>
                )}
              </div>
            )}

            {canEdit && (
              <button onClick={() => setShowFLForm(true)}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white rounded-xl py-2.5 text-sm font-medium mb-4">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Log Fuel
              </button>
            )}

            {fuelLogs.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-8">No fuel logs yet.</p>
            ) : (
              <div className="space-y-2">
                {fuelLogs.map((l) => (
                  <div key={l.id} className="flex items-center justify-between bg-background rounded-xl px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-text-primary">
                        {format(parseISO(l.log_date), 'MMM d, yyyy')}
                      </div>
                      <div className="text-xs text-text-muted">
                        {parseFloat(l.gallons).toFixed(3)} gal · ${parseFloat(l.price_per_gallon).toFixed(3)}/gal
                        {l.station ? ` · ${l.station}` : ''}
                        {l.mileage ? ` · ${l.mileage.toLocaleString()} mi` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-text-primary">
                        ${parseFloat(l.total_cost).toFixed(2)}
                      </span>
                      {canEdit && (
                        <button onClick={() => removeFL.mutate(l.id)}
                          className="text-text-muted hover:text-rose-500">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Reminders ── */}
        {tab === 'reminders' && (
          <div className="p-4">
            {canEdit && (
              <button onClick={() => { setEditingRm(null); setShowRmForm(true) }}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white rounded-xl py-2.5 text-sm font-medium mb-4">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add Reminder
              </button>
            )}
            {reminders.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-8">No reminders set.</p>
            ) : (
              <div className="space-y-2">
                {reminders.map((r) => (
                  <div key={r.id} className="bg-background rounded-xl px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-text-primary">{r.reminder_name}</span>
                        <ReminderStatus reminder={r} />
                      </div>
                      {canEdit && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => { setEditingRm(r); setShowRmForm(true) }}
                            className="text-xs text-primary font-medium">Edit</button>
                          <button onClick={() => removeRm.mutate(r.id)}
                            className="text-text-muted hover:text-rose-500">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                    {r.due_mileage && (
                      <p className="text-xs text-text-muted mt-0.5">Due at {r.due_mileage.toLocaleString()} mi</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showSRForm && (
        <ServiceRecordForm
          vehicleId={vehicle.id}
          onSave={(p) => createSR.mutateAsync(p)}
          onClose={() => setShowSRForm(false)}
        />
      )}
      {showFLForm && (
        <FuelLogForm
          onSave={(p) => createFL.mutateAsync(p)}
          onClose={() => setShowFLForm(false)}
        />
      )}
      {showRmForm && (
        <ReminderForm
          reminder={editingRm}
          onSave={(p) => editingRm
            ? updateRm.mutateAsync({ id: editingRm.id, ...p })
            : createRm.mutateAsync(p)}
          onClose={() => { setShowRmForm(false); setEditingRm(null) }}
        />
      )}
    </div>
  )
}
